import React, { useState } from 'react';
import { Cell, flexRender, Row, VisibilityState } from '@tanstack/react-table';
import { createStyles } from '@/contexts/theme-context';
import { DetailColumnId } from '@sales-portal/domain/pricing/screens/customers/types';
import {
  getCellAlignmentStyle,
  usePricingCellAlignmentStyles,
} from '@sales-portal/domain/pricing/blocs/hooks/use-pricing-cell-alignment-styles';
import {
  isDeltaColumn,
  isInputColumn,
  isLeftAlignedColumn,
} from '@sales-portal/domain/pricing/screens/customers/blocs/utils/table-ui';
import { Pressable } from 'react-native';
import { PricingCustomerDetailRow } from '@sales-portal/domain/pricing/screens/customers/blocs/transformers/use-customer-detail-transformer';
import { TableCell } from '@experiences/components/table/table-cell';
import { TableRow } from '@experiences/components/table/table-row';

interface PricingCustomerDetailTableRowProps {
  columnOrder?: string[];
  columnVisibility?: VisibilityState;
  hasChanges?: boolean;
  index: number;
  row: Row<PricingCustomerDetailRow>;
}

export const PricingCustomerDetailTableRow =
  React.memo<PricingCustomerDetailTableRowProps>(
    ({ hasChanges = false, index, row }) => {
      const [isHovered, setIsHovered] = useState(false);
      const [styles] = useStyles();
      const [alignmentStyles] = usePricingCellAlignmentStyles();

      const changedStyle =
        hasChanges && isHovered
          ? { ...styles.cellWithChanges, ...styles.cellWithChangesHovered }
          : hasChanges
            ? styles.cellWithChanges
            : undefined;

      const hoveredStyle = hasChanges
        ? changedStyle
        : isHovered
          ? styles.hovered
          : undefined;

      return (
        <Pressable
          onHoverIn={() => setIsHovered(true)}
          onHoverOut={() => setIsHovered(false)}
          tabIndex={-1}
          testID={`pricing-customer-detail-table-row-${row.index}`}
        >
          <TableRow index={index} key={row.id}>
            {row
              .getVisibleCells()
              .map((cell: Cell<PricingCustomerDetailRow, unknown>) => {
                const columnId = cell.column.id as DetailColumnId;
                const alignmentStyle = getCellAlignmentStyle({
                  isDelta: isDeltaColumn(columnId),
                  isLeftAligned: isLeftAlignedColumn(columnId),
                  styles: alignmentStyles,
                });
                const inputCellStyle =
                  hasChanges && isInputColumn(columnId)
                    ? styles.inputCell
                    : undefined;
                return (
                  <TableCell
                    cell={cell}
                    key={cell.id}
                    style={{
                      ...alignmentStyle,
                      ...hoveredStyle,
                      ...inputCellStyle,
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, {
                      ...cell.getContext(),
                      index,
                    })}
                  </TableCell>
                );
              })}
          </TableRow>
        </Pressable>
      );
    },
  );

const useStyles = createStyles((theme) => ({
  cellWithChanges: {
    backgroundColor: theme.sk.color.bg.highlight.blue.default,
    justifyContent: 'flex-start',
    paddingTop: 14,
  },
  cellWithChangesHovered: {
    backgroundColor: theme.sk.color.bg.highlight.blue.hover,
  },
  hovered: {
    backgroundColor: theme.sk.color.bg.primary.hover,
  },
  inputCell: {
    paddingTop: 8,
  },
}));
